import { JobAttachment } from 'services/api/file';
import { bytesToMegabytes } from 'helpers/utils';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faFileZipper,
  faImage,
  faDownload,
  faTrash,
  faFilePdf,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import { Dropdown } from 'react-bootstrap';
import dayjs from 'dayjs';

interface JobFileListItemProps {
  attachment: JobAttachment;
  className?: string;
  onDeleteFile?: (fileId: string) => void;
}

export const JobFileListItem = ({
  attachment,
  className,
  onDeleteFile
}: JobFileListItemProps) => {
  const isPDF = Boolean(
    (attachment.mimeType === 'application/pdf' ||
      attachment?.fileName?.toLowerCase().endsWith('.pdf')) ??
      false
  );
  const isImage = Boolean(attachment.mimeType?.startsWith('image/') ?? false);
  const isVideo = Boolean(attachment.mimeType?.startsWith('video/') ?? false);

  const renderFilePreview = () => {
    if (isPDF) {
      return (
        <div style={{ height: '400px' }}>
          <iframe
            src={`${attachment.fileUrl}#toolbar=0`}
            title={attachment.fileName}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '0.375rem',
              border: '1px solid #dee2e6'
            }}
          />
        </div>
      );
    } else if (isImage) {
      return (
        <img
          src={attachment.thumbnail ? attachment.thumbnail : attachment.fileUrl}
          alt={attachment.fileName}
          className="rounded-2 img-fluid cursor-pointer"
          style={{ width: '50%' }}
        />
      );
    } else if (isVideo) {
      return (
        <video
          src={attachment.fileUrl}
          controls
          className="rounded-2 w-100"
          style={{ maxHeight: '240px' }}
        />
      );
    }
    return null;
  };

  return (
    <div className={classNames(className, 'border-bottom border-300 py-4')}>
      <div>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center mb-1">
              <FontAwesomeIcon
                icon={
                  isPDF
                    ? faFilePdf
                    : isImage
                    ? faImage
                    : isVideo
                    ? faVideo
                    : attachment.mimeType === 'zip'
                    ? faFileZipper
                    : faFileLines
                }
                className={classNames('me-2 fs-9', {
                  'text-danger': isPDF,
                  'text-700': !isPDF
                })}
              />
              <p className="text-1000 mb-0 lh-1">{attachment.fileName}</p>
            </div>
            <div
              className={classNames('d-flex fs-9 text-700 flex-wrap', {
                'mb-3': attachment.thumbnail || isPDF
              })}
            >
              <span>{bytesToMegabytes(Number(attachment.size))} MB</span>
              <span className="text-400 mx-1">| </span>
              <a href="#!">{attachment?.creator?.lastName}</a>
              <span className="text-400 mx-1">| </span>
              <span className="text-nowrap">
                {dayjs(attachment.createdAt).format('DD/MM/YYYY hh:mm A')}
              </span>
            </div>

            {renderFilePreview()}
          </div>
          <RevealDropdownTrigger>
            <RevealDropdown>
              <Dropdown.Item href={attachment.fileUrl} target="_blank">
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Download
              </Dropdown.Item>
              {onDeleteFile && (
                <Dropdown.Item
                  onClick={() => onDeleteFile(attachment.id)}
                  className="text-danger"
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Delete
                </Dropdown.Item>
              )}
            </RevealDropdown>
          </RevealDropdownTrigger>
        </div>
      </div>
    </div>
  );
};
